import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, Layers, Activity, Search, Filter, ArrowRight,
    CheckCircle2, Clock, Bot, Sparkles, ExternalLink,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Loader2
} from "lucide-react";

export default function DetectionRules() {
    const navigate = useNavigate();
    const [rulesData, setRulesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedSourceFilter, setSelectedSourceFilter] = useState("All");
    const [selectedRule, setSelectedRule] = useState(null);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newRuleForm, setNewRuleForm] = useState({
        name: "",
        source: "FortiGate",
        type: "Perimeter Security",
        query: "",
        mitreId: "T1071",
        mitreName: "Application Layer Protocol"
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/detect/rules`, { headers });
            setRulesData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load detection rules:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRule = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: newRuleForm.name,
                source: newRuleForm.source,
                type: newRuleForm.type,
                query: newRuleForm.query,
                mitre: [{ id: newRuleForm.mitreId, name: newRuleForm.mitreName }]
            };
            const res = await axios.post(`${backendURL}/api/detect/rules`, payload, { headers });
            if (res.data.success) {
                setRulesData(prev => ({
                    ...prev,
                    rules: [res.data.rule, ...(prev?.rules || [])],
                    totalRules: (prev?.totalRules || 0) + 1
                }));
                setAddModalOpen(false);
                setNewRuleForm({
                    name: "",
                    source: "Microsoft Defender",
                    type: "Custom",
                    query: "",
                    mitreId: "T1566",
                    mitreName: "Phishing"
                });
            }
        } catch (err) {
            console.error("Failed to add rule:", err);
        }
    };

    const rules = rulesData?.rules || [];
    const filteredRules = rules.filter(r => {
        const matchesSearch =
            r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.source?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.mitre?.some(m => m.id?.toLowerCase().includes(searchQuery.toLowerCase()) || m.name?.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesSource = selectedSourceFilter === "All" || r.source === selectedSourceFilter;
        return matchesSearch && matchesSource;
    });

    if (loading && !rulesData) {
        return (
            <div className="w-full min-h-[500px] flex flex-col items-center justify-center space-y-4">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                <p className="text-slate-400 text-sm font-mono">Loading real-time detection rule inventory...</p>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6 pb-12 animate-in fade-in duration-300">
            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>03</span>
                <span>/</span>
                <span className="text-slate-400">Detection Rules</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4.5 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Every rule, and what it is really doing.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            All of your detection rules in one inventory, with the techniques they map to, how often they fire, and how many investigations they drive. The rules that earn their place become obvious, and so do the ones that do not.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>One inventory across every connected source.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Detections and investigations measured per rule.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Built on the work QueryTel already does on every alert.</span>
                        </div>
                    </div>

                    {/* Quick Navigation to MITRE Coverage */}
                    <div className="p-4 bg-[#111827] border border-white/[0.06] rounded-2xl space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase">Detection Engineering Posture</span>
                            <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">MITRE Matrix</span>
                        </div>
                        <p className="text-[12px] text-slate-400 leading-relaxed">
                            Evaluate blind spots against real-world threat actors with the 14-tactic radar chart.
                        </p>
                        <Link
                            to="/detection-coverage"
                            className="inline-flex items-center gap-1.5 text-[12px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                            View Detection Coverage Matrix →
                        </Link>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: KPI Cards + Rules Table (7.5 cols) ═══ */}
                <div className="lg:col-span-8 space-y-5">
                    
                    {/* Top 3 KPI Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-1">
                            <span className="text-[11.5px] font-medium text-slate-400 block">Total Detection Rules</span>
                            <span className="text-[26px] font-extrabold text-white font-mono">
                                {rulesData?.totalRules ?? rules.length}
                            </span>
                        </div>

                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-1">
                            <span className="text-[11.5px] font-medium text-slate-400 block">Triggered Alerts</span>
                            <span className="text-[26px] font-extrabold text-white font-mono">
                                {(rulesData?.triggeredAlerts ?? rules.reduce((acc, r) => acc + (r.recentDetections || 0), 0)).toLocaleString()}
                            </span>
                        </div>

                        <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-1">
                            <span className="text-[11.5px] font-medium text-slate-400 block">MITRE Coverage</span>
                            <span className="text-[26px] font-extrabold text-emerald-400 font-mono">
                                {rulesData?.mitreCoverage ?? 64}%
                            </span>
                        </div>
                    </div>

                    {/* Filter & Search Bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl p-3 shadow-sm">
                        <div className="flex items-center gap-2 flex-1">
                            {/* Search Input */}
                            <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 flex-1 focus-within:border-blue-500/40 transition-colors">
                                <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                                <input
                                    type="text"
                                    placeholder="Search rules, sources, MITRE IDs (e.g. T1566, Phishing)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                                />
                            </div>

                            {/* Source Filter Dropdown */}
                            <select
                                value={selectedSourceFilter}
                                onChange={(e) => setSelectedSourceFilter(e.target.value)}
                                className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-lg px-2.5 py-1.5 outline-none cursor-pointer"
                            >
                                <option value="All" className="bg-[#0F172A]">All Sources ({rules.length})</option>
                                {Array.from(new Set(rules.map(r => r.source).filter(Boolean))).map(src => (
                                    <option key={src} value={src} className="bg-[#0F172A]">
                                        {src} ({rules.filter(r => r.source === src).length})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2 self-start sm:self-auto">
                            <button
                                onClick={fetchRules}
                                disabled={loading}
                                className="p-2 bg-white/[0.03] hover:bg-white/[0.08] text-slate-400 hover:text-white rounded-lg border border-white/[0.06] transition-all"
                                title="Refresh"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-blue-400" : ""}`} />
                            </button>

                            <button
                                onClick={() => setAddModalOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all whitespace-nowrap"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add Detection Rule
                            </button>
                        </div>
                    </div>

                    {/* Unified Rules Table */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl overflow-hidden shadow-xl">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                    <tr>
                                        <th className="py-3 px-4">Source</th>
                                        <th className="py-3 px-4">Detection Rule Name</th>
                                        <th className="py-3 px-3">Type</th>
                                        <th className="py-3 px-4">MITRE</th>
                                        <th className="py-3 px-4 text-right">Recent Detections</th>
                                        <th className="py-3 px-4 text-right">Investigations</th>
                                        <th className="py-3 px-4 text-center">Signal / Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/[0.04] text-[12px]">
                                    {filteredRules.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="py-12 text-center text-slate-500 italic">
                                                No detection rules match your filter criteria.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRules.map((rule) => {
                                            const hasActivity = (rule.recentDetections || 0) > 0;
                                            const conversionRate = hasActivity
                                                ? Math.round(((rule.investigations || 0) / rule.recentDetections) * 100)
                                                : null;
                                            const isHighSignal = conversionRate !== null && conversionRate >= 40;
                                            const badgeText = rule.badge === "QueryTel" ? "QT" : (rule.badge || rule.source?.substring(0, 2).toUpperCase() || "QT");

                                            return (
                                                <tr
                                                    key={rule.id}
                                                    onClick={() => setSelectedRule(rule)}
                                                    className="hover:bg-white/[0.02] cursor-pointer transition-colors group"
                                                >
                                                    {/* Source Badge & Name */}
                                                    <td className="py-3.5 px-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-2.5">
                                                            <div
                                                                className="w-7 h-7 rounded-md flex items-center justify-center font-mono font-bold text-[10px] text-white shadow-sm flex-shrink-0"
                                                                style={{ backgroundColor: rule.color || "#4F46E5" }}
                                                            >
                                                                {badgeText}
                                                            </div>
                                                            <span className="font-semibold text-slate-200">
                                                                {rule.source}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    {/* Rule Name */}
                                                    <td className="py-3.5 px-4">
                                                        <span className="font-bold text-white group-hover:text-blue-400 transition-colors">
                                                            {rule.name}
                                                        </span>
                                                    </td>

                                                    {/* Type */}
                                                    <td className="py-3.5 px-3 whitespace-nowrap">
                                                        <span className="text-[10px] font-mono uppercase bg-white/[0.04] text-slate-400 px-2 py-0.5 rounded border border-white/[0.06]">
                                                            {rule.type}
                                                        </span>
                                                    </td>

                                                    {/* MITRE Techniques */}
                                                    <td className="py-3.5 px-4">
                                                        <div className="space-y-1">
                                                            {rule.mitre?.map((m, mIdx) => (
                                                                <Link
                                                                    key={mIdx}
                                                                    to="/detection-coverage"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="block text-[11px] text-blue-400 hover:text-blue-300 hover:underline leading-tight"
                                                                >
                                                                    {m.id} - {m.name}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </td>

                                                    {/* Recent Detections */}
                                                    <td className="py-3.5 px-4 text-right font-mono font-bold text-slate-200">
                                                        {(rule.recentDetections || 0).toLocaleString()}
                                                    </td>

                                                    {/* Investigations */}
                                                    <td className="py-3.5 px-4 text-right font-mono font-bold text-white">
                                                        {(rule.investigations || 0).toLocaleString()}
                                                    </td>

                                                    {/* Signal Conversion & Actions */}
                                                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                                        <div className="flex items-center justify-center gap-2">
                                                            {hasActivity ? (
                                                                <span className={`text-[10.5px] font-mono font-bold px-2 py-0.5 rounded border ${
                                                                    isHighSignal
                                                                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                                                        : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                                                                }`}>
                                                                    {conversionRate}% Signal
                                                                </span>
                                                            ) : (
                                                                <span className="text-[10px] font-mono text-slate-400 bg-white/[0.04] px-2 py-0.5 rounded border border-white/[0.08]">
                                                                    Active Policy
                                                                </span>
                                                            )}
                                                            <Link
                                                                to={`/detection-rules/${rule.id}/recommendations`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="px-2 py-0.5 bg-blue-500/10 hover:bg-blue-600 hover:text-white text-blue-400 border border-blue-500/20 rounded text-[10.5px] font-bold transition-all flex items-center gap-1"
                                                            >
                                                                <Sparkles className="w-3 h-3" />
                                                                Tune
                                                            </Link>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>

            </div>

            {/* ─── Selected Rule Detail Slide-Over Drawer ─── */}
            {selectedRule && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end animate-in fade-in duration-200">
                    <div className="bg-[#0F172A] border-l border-white/[0.08] w-full max-w-lg p-6 flex flex-col justify-between shadow-2xl animate-in slide-in-from-right duration-200">
                        <div className="space-y-5 overflow-y-auto custom-scrollbar pr-1">
                            
                            {/* Drawer Header */}
                            <div className="flex items-start justify-between border-b border-white/[0.06] pb-4">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="w-6 h-6 rounded flex items-center justify-center font-mono font-bold text-[9px] text-white"
                                            style={{ backgroundColor: selectedRule.color || "#4F46E5" }}
                                        >
                                            {selectedRule.badge === "QueryTel" ? "QT" : (selectedRule.badge || selectedRule.source?.substring(0, 2).toUpperCase() || "QT")}
                                        </div>
                                        <span className="text-[11px] font-mono text-slate-400">{selectedRule.source}</span>
                                    </div>
                                    <h3 className="text-[17px] font-extrabold text-white">{selectedRule.name}</h3>
                                </div>
                                <button onClick={() => setSelectedRule(null)} className="text-slate-400 hover:text-white">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Performance Metrics */}
                            <div className="grid grid-cols-2 gap-3 text-[12px]">
                                <div className="p-3 bg-black/40 rounded-xl border border-white/[0.04]">
                                    <span className="text-[10px] text-slate-500 font-mono uppercase block">Total Fired</span>
                                    <span className="text-[18px] font-bold text-white font-mono">{selectedRule.recentDetections || 0}</span>
                                </div>
                                <div className="p-3 bg-black/40 rounded-xl border border-white/[0.04]">
                                    <span className="text-[10px] text-slate-500 font-mono uppercase block">Investigations</span>
                                    <span className="text-[18px] font-bold text-emerald-400 font-mono">{selectedRule.investigations || 0}</span>
                                </div>
                            </div>

                            {/* MITRE Mapping */}
                            <div className="space-y-2">
                                <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 block">
                                    Covered MITRE ATT&CK Techniques
                                </span>
                                <div className="space-y-1.5">
                                    {selectedRule.mitre?.map((m, idx) => (
                                        <div key={idx} className="p-2.5 bg-blue-500/[0.06] border border-blue-500/20 rounded-lg flex items-center justify-between text-[11.5px]">
                                            <span className="text-blue-300 font-medium">{m.id} - {m.name}</span>
                                            <Link to="/detection-coverage" className="text-blue-400 hover:text-white">
                                                <ExternalLink className="w-3.5 h-3.5" />
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Detection Logic / Query */}
                            <div className="space-y-2">
                                <span className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 block">
                                    Detection Query Logic
                                </span>
                                <pre className="p-3.5 bg-black/60 border border-white/[0.06] rounded-xl text-[11px] font-mono text-emerald-400 overflow-x-auto custom-scrollbar leading-relaxed">
                                    {selectedRule.query || "// Autonomous rule managed by QueryTel Engine"}
                                </pre>
                            </div>

                        </div>

                        {/* Drawer Actions */}
                        <div className="pt-4 border-t border-white/[0.06] flex flex-col sm:flex-row items-center justify-between gap-2.5">
                            <button
                                onClick={() => {
                                    navigate(`/detection-rules/${selectedRule.id}/recommendations`);
                                }}
                                className="w-full sm:flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-[12px] font-bold shadow-md transition-all text-center flex items-center justify-center gap-1.5"
                            >
                                <Sparkles className="w-3.5 h-3.5" />
                                QueryTel Recommendations
                            </button>
                            <button
                                onClick={() => {
                                    navigate("/investigations");
                                }}
                                className="w-full sm:w-auto px-4 py-2.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-xl text-[12px]"
                            >
                                Linked Alerts
                            </button>
                            <button
                                onClick={() => setSelectedRule(null)}
                                className="w-full sm:w-auto px-3 py-2.5 text-slate-400 hover:text-white text-[12px]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Add Detection Rule Modal ─── */}
            {addModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Add Detection Rule to Inventory</h3>
                            <button onClick={() => setAddModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateRule} className="space-y-3.5 text-[12px]">
                            <div>
                                <label className="text-slate-400 block mb-1">Rule Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. CobaltStrike Named Pipe Beaconing"
                                    value={newRuleForm.name}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Source Connector</label>
                                    <select
                                        value={newRuleForm.source}
                                        onChange={(e) => setNewRuleForm({ ...newRuleForm, source: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="Microsoft Defender">Microsoft Defender</option>
                                        <option value="QueryTel">QueryTel</option>
                                        <option value="Splunk">Splunk</option>
                                        <option value="CrowdStrike">CrowdStrike</option>
                                        <option value="FortiGate">FortiGate</option>
                                        <option value="SentinelOne">SentinelOne</option>
                                        <option value="AWS">AWS</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="text-slate-400 block mb-1">Rule Type</label>
                                    <select
                                        value={newRuleForm.type}
                                        onChange={(e) => setNewRuleForm({ ...newRuleForm, type: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="Custom">Custom</option>
                                        <option value="OOTB">OOTB (Vendor)</option>
                                        <option value="QueryTel Autonomous">QueryTel Autonomous</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">MITRE Technique ID</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. T1071"
                                        value={newRuleForm.mitreId}
                                        onChange={(e) => setNewRuleForm({ ...newRuleForm, mitreId: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono text-[11px]"
                                    />
                                </div>

                                <div>
                                    <label className="text-slate-400 block mb-1">Technique Name</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Application Layer Protocol"
                                        value={newRuleForm.mitreName}
                                        onChange={(e) => setNewRuleForm({ ...newRuleForm, mitreName: e.target.value })}
                                        className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Rule Query / Logic</label>
                                <textarea
                                    rows="3"
                                    placeholder="e.g. SecurityEvent | where EventID == 4688 and ProcessCommandLine has '-enc'"
                                    value={newRuleForm.query}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, query: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono text-[11px] leading-relaxed"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setAddModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Add Rule
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
